import React, { useContext, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import {
  CNC_GET_ORGANIZATIONS,
  type CncGetOrganizationsData,
} from "./CustomerProvisioningApi";
import useCncReachable from "./useCncReachable/useCncReachable";
import { copyJWTLink, pasteJWTLink } from "./JWTInjectionUtils";
import LaunchPublicAction from "./LaunchPublicActions/LaunchPublicAction";
import {
  TextButton,
  ModalContext,
  AlertIndicator,
  alertTypes,
  PageHeader,
  type NavigationBreadcrumbItem,
} from "@SeemplicityDev/shared";
import AccountsTable from "./AccountsTable";
import OrphanOrganizationsTable from "./OrphanOrganizationsTable";
import NewOrganization from "./NewOrganization/NewOrganization";

import type { AccountType } from "../utils";

import "./CustomerProvisioning.scss";

// TODO unify: AccountsTable / OrphanOrganizationsTable are converted in parallel;
// once their prop types are exported, import the row shapes from there instead.
interface FlatAccountRow {
  organizationSlug: string;
  organizationName: string;
  accountUuid: string;
  accountName: string;
  ssoEnforced: boolean;
  domains: string[];
  primaryDomain: string;
  accountEnabled: boolean;
  organizationEnabled: boolean;
  databaseIdentifier: string;
  databaseSchema: string;
  accountType: AccountType;
  canDeleteOrganization: boolean;
}

interface OrphanOrganizationRow {
  organizationSlug: string;
  organizationName: string;
  ssoEnforced: boolean;
  domains: string[];
  primaryDomain: string;
  enabled: boolean;
}

const BREADCRUMBS: NavigationBreadcrumbItem[] = [
  { label: "Organizations and Accounts" },
];

const CustomerProvisioning: React.FC = () => {
  const { openModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const { loading, data: organizationsData } =
    useQuery<CncGetOrganizationsData>(CNC_GET_ORGANIZATIONS);

  // Don't show the Impersonate button if the CNC endpoint is unreachable.
  const cncReachable = useCncReachable();
  const [cncAlertDismissed, setCncAlertDismissed] = useState(false);

  const [flatAccounts, orgsWithoutAccounts] = useMemo<
    [FlatAccountRow[], OrphanOrganizationRow[]]
  >(() => {
    const organizations = organizationsData?.cnc_organizations || [];
    if (organizations.length === 0) {
      return [[], []];
    }
    const accounts: FlatAccountRow[] = [];
    const orgsWithoutAccounts: OrphanOrganizationRow[] = [];
    for (let organization of organizations) {
      if (organization.accounts.length === 0) {
        orgsWithoutAccounts.push({
          organizationSlug: organization.slug,
          organizationName: organization.name,
          ssoEnforced: !organization.password_enabled,
          domains: organization.domains ?? [],
          primaryDomain: organization.primary_domain,
          enabled: organization.enabled,
        });
      }
      // Can delete organization if it is enabled and has no enabled accounts
      const canDeleteOrganization =
        organization.enabled &&
        organization.accounts.filter((account) => account.enabled).length === 0;
      for (let account of organization.accounts) {
        accounts.push({
          organizationSlug: organization.slug,
          organizationName: organization.name,
          accountUuid: account.id,
          accountName: account.name,
          ssoEnforced: !organization.password_enabled,
          domains: organization.domains ?? [],
          primaryDomain: organization.primary_domain,
          accountEnabled: account.enabled,
          organizationEnabled: organization.enabled,
          databaseIdentifier: account.database_identifier,
          databaseSchema: account.database_schema,
          accountType: account.account_type,
          canDeleteOrganization,
        });
      }
    }
    return [accounts, orgsWithoutAccounts];
  }, [organizationsData]);

  return (
    <div className={"customer-provisioning-wrapper"}>
      <div className="app-view-top-bar">
        <PageHeader
          breadcrumbs={BREADCRUMBS}
          actions={
            <div className="cnc-buttons-wrap">
              <a href={copyJWTLink} rel="noreferrer">
                <TextButton
                  wrapperClassName={"copy-jwt-btn"}
                  data-testid={"copy-jwt"}
                  label={t("copy jwt")}
                  buttonStyle={"textLink"}
                />
              </a>
              <a href={pasteJWTLink} rel="noreferrer">
                <TextButton
                  wrapperClassName={"paste-jwt-btn"}
                  data-testid={"paste-jwt"}
                  label={t("paste jwt")}
                  buttonStyle={"textLink"}
                />
              </a>
              <TextButton
                data-testid={"public-actions"}
                buttonStyle={"primary"}
                label={t("Public Actions")}
                onClick={() => {
                  openModal({
                    title: t("Launch Public Actions"),
                    size: "m",
                    overlay: true,
                    component: <LaunchPublicAction />,
                  });
                }}
              />
            </div>
          }
        />
      </div>
      {!cncReachable && !cncAlertDismissed && (
        <AlertIndicator
          wrapperClassName={"cnc-unreachable-alert"}
          data-testid={"cnc-unreachable-alert"}
          type={alertTypes.ERROR}
          showClose
          onClose={() => setCncAlertDismissed(true)}
          title={
            "Can't impersonate customers. Tailscale isn't running properly."
          }
          content={
            <ol className={"cnc-unreachable-alert-steps"}>
              <li>Check that Tailscale is on and running.</li>
              <li>
                If it's still not working, turn it off and on again, then
                refresh the page.
              </li>
              <li>If the error keeps recurring, contact IT for help.</li>
            </ol>
          }
        />
      )}
      <div className={"tenant-table-container"}>
        <h3>Accounts</h3>
        <AccountsTable
          loading={loading}
          accounts={flatAccounts}
          cncReachable={cncReachable}
        />
        {orgsWithoutAccounts.length > 0 && (
          <>
            <h3>Organizations with no accounts</h3>
            <OrphanOrganizationsTable
              loading={loading}
              organizations={orgsWithoutAccounts}
            />
          </>
        )}
        <TextButton
          wrapperClassName={"create-tenant-btn"}
          data-testid={"create-tenant"}
          buttonStyle={"primary"}
          label={t("+ New Organization")}
          size={"large"}
          onClick={() => {
            openModal({
              title: t("New Organization"),
              size: "xs",
              overlay: true,
              component: <NewOrganization />,
            });
          }}
        />
      </div>
    </div>
  );
};
export default CustomerProvisioning;
